import { Link, useRouterState } from '@tanstack/react-router'
import { type ReactNode } from 'react'

import { cn } from '@aeris/ui'

import { useAuth } from '../lib/auth-context.ts'
import { tabLabelOf, tabsOf } from '../lib/config-tabs.ts'
import { useWorkspace, type WorkspaceModule } from '../lib/workspace.ts'

/**
 * A trilha de abas das duas páginas de configuração (F068).
 *
 * ## Por que não é o `Tabs` do kit
 *
 * O `Tabs` do Radix guarda a aba num estado de React e desmonta o painel
 * inativo. Aqui a aba **é uma rota**: `/atendimento-canais` continua sendo o
 * endereço da tela de canais, com favorito, botão de voltar, janela do Modo PC
 * e link vindo do guia de configuração. Um `Tabs` de verdade jogaria tudo isso
 * fora e trocaria por um estado que não sobrevive a um F5.
 *
 * Então a trilha é um `<nav>` de links de verdade, com a aparência da trilha do
 * kit. O que ela não precisa é do `role="tablist"`: um tablist promete que as
 * setas do teclado trocam de painel sem sair da página, e aqui cada seta seria
 * uma navegação. Uma lista de links faz exatamente o que parece que faz.
 *
 * ## As abas obedecem à permissão, uma por uma
 *
 * `allows` mais `showsPage` — as mesmas duas funções que a barra lateral usa.
 * É isto que impede que juntar catorze telas numa vire um buraco de permissão:
 * o admin que negou "Custos de atendimento" para o João continua negando, e o
 * João não vê a aba.
 */

export const ConfigTabs = ({ hub }: { hub: WorkspaceModule }): ReactNode => {
  const { allows, showsPage } = useAuth()
  const workspace = useWorkspace()
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })

  const visible = tabsOf(hub).filter(
    (entry) =>
      (entry.permission === undefined || allows(entry.permission)) &&
      showsPage(entry.module)
  )

  if (visible.length === 0) return null

  return (
    <nav
      aria-label="Seções da configuração"
      // Rola na horizontal em vez de quebrar em duas linhas: oito abas numa
      // tela estreita viram duas fileiras que empurram o conteúdo para baixo
      // toda vez que a janela encolhe.
      className="-mx-1 overflow-x-auto px-1"
    >
      <ul className="inline-flex w-max gap-1 rounded-(--radius-md) border border-(--border-subtle) p-1">
        {visible.map((entry) => {
          const href = workspace.routeFor(entry.module)
          const active = pathname.startsWith(href)

          return (
            <li key={entry.module}>
              <Link
                to={href}
                // `aria-current` é o que o leitor de tela anuncia; a cor
                // abaixo é para quem enxerga, e nunca sozinha (docs/UI_UX.md).
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'block rounded-(--radius-sm) px-3 py-1.5 text-(length:--text-sm) whitespace-nowrap',
                  'transition-colors duration-(--duration-fast)',
                  'text-(--text-secondary) hover:text-(--text-primary)',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--brand-primary)',
                  // Mais leve que o preenchimento sólido (F132): a aba ativa
                  // era a coisa mais forte da tela, acima do título e do botão
                  // principal da própria aba.
                  active &&
                    'bg-(--brand-primary-subtle) font-(--weight-medium) text-(--brand-primary)'
                )}
              >
                {tabLabelOf(entry)}
              </Link>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

/**
 * O cabeçalho de uma aba.
 *
 * A `PageHeader` agora pertence à página de configuração, não à tela — senão
 * seriam dois títulos empilhados a cada aba. O que a tela ainda precisa dizer
 * é o que ela é, e oferecer a própria ação ("Nova conta", "Novo modelo"), que
 * é diferente em cada uma.
 *
 * `h2` e não `h1`: o `h1` é o da página inteira, e dois `h1` numa página
 * quebram a lista de cabeçalhos que um leitor de tela usa para navegar.
 */
export const TabHeader = ({
  title,
  description,
  actions,
}: {
  title: string
  description?: ReactNode
  actions?: ReactNode
}): ReactNode => (
  <div className="flex flex-wrap items-start gap-3">
    <div className="flex min-w-0 flex-col gap-0.5">
      <h2 className="text-(length:--text-base) font-(--weight-semibold) text-(--text-primary)">
        {title}
      </h2>
      {description === undefined ? null : (
        <p className="text-(length:--text-sm) text-(--text-secondary)">
          {description}
        </p>
      )}
    </div>
    {actions === undefined ? null : (
      <div className="ml-auto flex flex-wrap items-center gap-2">{actions}</div>
    )}
  </div>
)
